import {
  FaFileMedical,
  FaUserMd,
  FaVials,
  FaMapMarkedAlt,
  FaSitemap,
  FaExclamationTriangle,
  FaFileInvoice,
  FaPalette,
  FaChartPie,
  FaUserInjured,
  FaNetworkWired,
  FaImages,
  FaCodeBranch,
  FaShoppingCart,
  FaSortAmountDown,
  FaHome,
  FaTasks,
  FaCrop,
  FaFileCode,
} from "react-icons/fa";

/**
 * Single source of truth for sidebar navigation.
 * Paths match the current navbar exactly for 100% logic preservation.
 * 
 * accessKey: Links to AccessContext keys for conditional access control
 * - "productRecommendations": Based on showAdditionalRecommendationsPage flag
 * - "vizappConfiguration": Based on reportAddons containing Viz App type
 * - "assetManagement": Based on dynamicCoverPage flag
 */
export const NAV_GROUPS = [
  {
    label: "Home",
    items: [
      { path: "/", label: "Home", icon: <FaHome /> },
      { path: "/onboarding-status", label: "Onboarding", icon: <FaTasks /> },
      { path: "/sample-json", label: "Get Latest Data", icon: <FaFileCode /> },
    ],
  },
  {
    label: "Mapping",
    items: [
      { path: "/profile-mapping", label: "Profile Mapping", icon: <FaMapMarkedAlt /> },
      { path: "/parameter-mapping", label: "Parameter Mapping", icon: <FaSitemap /> },
      { path: "/unmapped-management", label: "Parameter Mapping Auto", icon: <FaExclamationTriangle /> },
    ],
  },
  {
    label: "Configuration",
    items: [
      { path: "/report-configuration", label: "Report Configuration", icon: <FaFileInvoice /> },
      { path: "/color-configuration", label: "Color Configuration", icon: <FaPalette />, accessKey: "colorConfiguration" },
      { path: "/vizapp-configuration", label: "Vizapp Configuration", icon: <FaChartPie />, accessKey: "vizappConfiguration" },
      { path: "/patient-details-configuration", label: "PatientDetails Config", icon: <FaUserInjured /> },
      { path: "/webhook-configuration", label: "Webhook Configuration", icon: <FaNetworkWired /> },
      { path: "/header-footer", label: "Header & Footer", icon: <FaCrop /> },
    ],
  },
  {
    label: "Content",
    items: [
      { path: "/sample-report-generation", label: "Sample Report", icon: <FaFileMedical /> },
      { path: "/profile-content", label: "Profile Content", icon: <FaUserMd />, accessKey: "profileContent" },
      { path: "/parameter-content", label: "Parameter Content", icon: <FaVials />, accessKey: "parameterContent" },
    ],
  },
  {
    label: "Management",
    items: [
      { path: "/asset-management", label: "Asset Management", icon: <FaImages />, accessKey: "assetManagement" },
      { path: "/version-management", label: "Version Management", icon: <FaCodeBranch />, accessKey: "versionManagement" },
      { path: "/product-recommendations", label: "Product Recommendations", icon: <FaShoppingCart />, accessKey: "productRecommendations" },
      { path: "/ordering", label: "Profile/Parameter Ordering", icon: <FaSortAmountDown /> },
    ],
  },
];

export const LOGO_SRC =
  "https://niroggyan.s3.ap-south-1.amazonaws.com/new-billing-dashboard-files/logos/niroggyan-logo.svg";

export const LOGO_COLLAPSED_SRC = "/niroggyan-logo-collapsed.svg";

/**
 * Paths that are hidden in Admin Mode (toggle = Admin).
 * These tabs require a real client login context and make no sense
 * at the admin level without a client selected.
 * They are fully visible in Client Mode when a client is selected.
 */
export const ADMIN_HIDDEN_PATHS = new Set([
  '/onboarding-status',        // Onboarding
  '/sample-json',              // Get Latest Data
  '/webhook-configuration',    // Webhook Configuration
  '/asset-management',         // Asset Management
  '/product-recommendations',  // Product Recommendations
  '/sample-report-generation', // Sample Report
]);


/**
 * Admin-only navigation group.
 * These tabs are ALWAYS shown when the user is an admin,
 * even when no client is selected — they call admin-level
 * APIs without a clientId (buildRequestConfig passes nothing
 * when no client is chosen in sessionStorage).
 *
 * Tabs here appear in a dedicated "Admin Tools" section above
 * the client config tabs, with a purple accent separator.
 */
export const ADMIN_NAV_GROUPS = [
  {
    label: "Admin Tools",
    items: [
      {
        path: "/profile-mapping",
        label: "Profile Mapping",
        icon: <FaMapMarkedAlt />,
        description: "Admin-level profile mapping",
      },
      {
        path: "/parameter-mapping",
        label: "Parameter Mapping",
        icon: <FaSitemap />,
        description: "Admin-level parameter mapping",
      },
      {
        path: "/unmapped-management",
        label: "Parameter Mapping Auto",
        icon: <FaExclamationTriangle />,
        description: "Auto-detect unmapped parameters",
      },
      {
        path: "/ordering",
        label: "Profile/Parameter Ordering",
        icon: <FaSortAmountDown />,
        description: "Order profiles and parameters",
      },
    ],
  },
];
